'use client';

import { useRef, useEffect, useState } from 'react';
import Link from 'next/link';
import {
  motion,
  useInView,
  useMotionValue,
  useTransform,
  animate,
  useReducedMotion,
} from 'framer-motion';
import { ArrowRight, Check, Shield, Lock, MapPin, X, Building, Users, Mail, Info, Sparkles, ChevronDown } from 'lucide-react';

import { LandingNavbar } from '@/components/landing-navbar';
import { trackEvent } from '@/lib/analytics';
import { LandingFooter } from '@/components/landing-footer';
import { VoiceChallenge } from '@/components/voice-challenge';
import { PhoneSimulator } from '@/components/phone-simulator';
import { FamilyRiskQuiz } from '@/components/family-risk-quiz';
import { learnArticles, CATEGORY_META } from '@/data/learn-articles';
import { api } from '@/lib/api';
import { supabase } from '@/lib/supabase';

/* ═══════════════════════════════════════════════════════════════════
   TRUSTBOX LANDING — v8 "Flowing Zones"
   Zone 1 (dark-green #0A1F12): Hero (split) + Connector + Stats + Quote
   Zone 2 (cream #FAFAF7):      How It Works + Pricing + Objections
   Zone 3 (dark-green #0A1F12): Footer

   Persona: Sarah, 43 — "The Worried Daughter"
   ═══════════════════════════════════════════════════════════════════ */

/* ── Colors ── */
const DARK_BG = '#0A1F12';
const CREAM_BG = '#FAFAF7';

/* ── Animation config ── */
const EASE = [0.16, 1, 0.3, 1] as const;

const FADE_UP = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: EASE },
};

const makeStagger = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.7, delay, ease: EASE },
});

/* ── CountUp component ── */
function CountUp({
  target,
  suffix = '',
  prefix = '',
  decimals = 0,
}: {
  target: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => {
    if (decimals > 0) return v.toFixed(decimals);
    return Math.round(v).toString();
  });

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(count, target, {
      duration: 2,
      ease: 'easeOut',
    });
    return controls.stop;
  }, [isInView, target, count]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      <motion.span>{rounded}</motion.span>
      {suffix}
    </span>
  );
}

/* ── Section Connector (waveform bars between sections) ── */
function SectionConnector({ color = '#22C55E' }: { color?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: false, margin: '-20%' });
  const shouldReduceMotion = useReducedMotion();

  const barHeights = [6, 12, 18, 12, 6];

  return (
    <div
      ref={ref}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0,
        padding: '20px 0',
        position: 'relative',
        zIndex: 5,
      }}
    >
      {/* Top line */}
      <div
        style={{
          width: 2,
          height: 40,
          background: `linear-gradient(to bottom, ${color}4D, ${color}14)`,
        }}
      />
      {/* Waveform bars */}
      <div style={{ display: 'flex', gap: 2, alignItems: 'center', height: 16 }}>
        {barHeights.map((h, i) => (
          <div
            key={`cw-${i}`}
            style={{
              width: 3,
              height: h,
              borderRadius: 3,
              background: color,
              opacity: isInView ? 0.5 : 0.2,
              transformOrigin: 'center',
              animation: isInView && !shouldReduceMotion ? `connector-pulse 1s ease-in-out ${i * 0.1}s infinite` : 'none',
              transition: 'opacity 0.4s',
            }}
          />
        ))}
      </div>
      {/* Bottom line */}
      <div
        style={{
          width: 2,
          height: 40,
          background: `linear-gradient(to bottom, ${color}14, ${color}4D)`,
        }}
      />
    </div>
  );
}

/* ── Tag waveform bars (inline, small, custom) ── */
function TagWaveform() {
  const heights = [6, 12, 16, 10, 14];
  const delays = [0, 0.15, 0.3, 0.45, 0.6];

  return (
    <span style={{ display: 'flex', gap: 2, alignItems: 'center', height: 18 }}>
      {heights.map((h, i) => (
        <span
          key={`tw-${i}`}
          style={{
            width: 3,
            height: h,
            borderRadius: 3,
            background: '#1DB954',
            display: 'block',
            transformOrigin: 'center',
            animation: `tag-wave-pulse 1.2s ease-in-out ${delays[i]}s infinite`,
          }}
        />
      ))}
    </span>
  );
}

/* ── Mother's Day Countdown Banner (fixed top, above navbar) ── */
const BANNER_HEIGHT = 36;

function MothersDayCountdown({ onEnd }: { onEnd: () => void }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0 });
  useEffect(() => {
    const calc = () => {
      const diff = new Date('2026-05-10T00:00:00').getTime() - Date.now();
      if (diff <= 0) {
        onEnd();
        return;
      }
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
      });
    };
    calc();
    const iv = setInterval(calc, 60000);
    return () => clearInterval(iv);
  }, [onEnd]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: BANNER_HEIGHT,
        background: 'linear-gradient(90deg, #78350F 0%, #92400E 50%, #78350F 100%)',
        borderBottom: '1px solid rgba(252,211,77,0.3)',
        padding: '0 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        fontSize: '0.8125rem',
        color: '#FCD34D',
        fontWeight: 600,
        zIndex: 200,
        letterSpacing: '0.2px',
      }}
    >
      <span>⏰</span>
      <span className="banner-text">
        Mother&apos;s Day is Sunday. Only{' '}
        <span style={{ fontFamily: 'monospace', color: '#fff' }}>
          {t.d}d {t.h}h {t.m}m
        </span>{' '}
        left to protect your family.
      </span>
      <Link
        href="/try"
        onClick={() =>
          trackEvent('landing_cta_clicked', {
            ctaLabel: 'topbar_try_demo',
            destination: '/try',
          })
        }
        style={{
          color: '#fff',
          textDecoration: 'underline',
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
      >
        Try free demo →
      </Link>
      <style>{`
        @media (max-width: 640px) {
          .banner-text { font-size: 11px; }
        }
      `}</style>
    </div>
  );
}

/* ── Scroll Progress Bar ── */
function ScrollProgressBar() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    function handleScroll() {
      const scrollY = window.scrollY;
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      if (docH <= 0) return;
      setProgress(Math.min(scrollY / docH, 1));
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: 3,
        width: `${progress * 100}%`,
        background: 'linear-gradient(90deg, #22C55E, #1DB954)',
        zIndex: 200,
        pointerEvents: 'none',
        boxShadow: '0 0 8px rgba(34,197,94,0.4)',
      }}
    />
  );
}

/* ── How It Works steps ── */
const STEPS = [
  {
    num: 1,
    title: 'Record your voice sample',
    body: 'Read a short script aloud for 15 seconds. That is all our secure system needs to generate a highly realistic clone of your voice.',
  },
  {
    num: 2,
    title: 'Hear your own clone first',
    body: 'Listen to your voice clone before doing anything else. If you can scarcely tell it is fake, your parent won\'t be able to either.',
  },
  {
    num: 3,
    title: 'Use the safe test call as an icebreaker',
    body: 'We place a single, TCPA-compliant educational phone call using your cloned voice. The call concludes with a gentle, automated reveal explaining it is a safe test.',
  },
  {
    num: 4,
    title: 'Establish a family safeword',
    body: 'Access your personalized safety report and talking points to easily agree on a family safeword, building real defense without the lecturing.',
  },
];

/* ── Pricing checklist ── */
const PRICING_CHECKS = [
  'AI voice clone of your voice',
  'Real phone call to your family member',
  'Awareness score + safety report',
  'Voice data deleted within 24 hours',
];

/* ── FAQs (10 — matches FAQPage schema 1:1, optimized for AI citation) ── */
type FaqTag = 'BRAND' | 'PRIVACY' | 'TRUST' | 'EDUCATIONAL';
const FAQS: { q: string; a: React.ReactNode; tag: FaqTag }[] = [
  {
    tag: 'EDUCATIONAL',
    q: 'Why not just talk to my parents about voice scams instead of paying $4.90?',
    a: (
      <>
        Telling someone about a threat creates awareness; <strong>experiencing it safely creates a lasting memory</strong>. People who have personally heard a convincing AI voice clone are <strong>far less likely to be deceived</strong> because their brain has already processed what the shock feels like. No lecture or brochure replicates hearing a perfect clone of your own child’s voice asking for help. TrustboxAI delivers that direct experience safely, with a gentle automated reveal and a personalized safety report.
      </>
    ),
  },
  {
    tag: 'TRUST',
    q: 'What if my parent gets upset? How does this help start the conversation?',
    a: (
      <>
        Trustbox is designed with guidance from <strong>security psychologists</strong> to educate, not terrify. The simulated call uses a gentle, reassuring tone and immediately transitions into a warm reveal: <em>“This was a safe test from someone who loves you.”</em> Because they experience the voice clone in a controlled environment, the subsequent conversation is driven by <strong>mutual relief, curiosity, and collaboration</strong> &mdash; avoiding the awkwardness or defensiveness that usually happens when discussing security.
      </>
    ),
  },
  {
    tag: 'BRAND',
    q: 'What is TrustboxAI and how does it work?',
    a: (
      <>
        TrustboxAI is a secure voice scam simulation service. We clone your voice from a 15-second recording, then place a <strong>safe, educational phone call</strong> to your family member using that clone. At the end of the call, a <strong>gentle reveal</strong> explains it was a test arranged by someone who loves them. You receive an awareness score and safety tips. The simulation costs $4.90, requires no subscription, and <strong>all voice data is deleted within 24 hours</strong>.
      </>
    ),
  },
  {
    tag: 'BRAND',
    q: 'Is TrustboxAI legitimate and safe to use?',
    a: (
      <>
        Yes. TrustboxAI is <strong>fully TCPA compliant</strong>, makes only one educational call to the number you provide, and <strong>permanently deletes all voice recordings within 24 hours</strong>. The service operates legally in the US and Canada. The simulation ends with a clear, compassionate reveal so your family member understands it was a test &mdash; not a real scare.
      </>
    ),
  },
  {
    tag: 'BRAND',
    q: 'How much does TrustboxAI cost? Is there a free option?',
    a: (
      <>
        A single TrustboxAI simulation costs <strong>$4.90 &mdash; one-time, with no hidden subscriptions</strong>. If your family member does not answer the call, you receive a <strong>full refund automatically</strong>. <Link href="/try" style={{ color: '#166534', textDecoration: 'underline', fontWeight: 600, marginLeft: 4 }}>Try the free voice clone demo first &rarr;</Link>
      </>
    ),
  },
  {
    tag: 'PRIVACY',
    q: 'Does TrustboxAI store my voice or my family member’s phone number?',
    a: (
      <>
        No. TrustboxAI <strong>automatically deletes all voice clone data within 24 hours</strong> of your simulation. Your family member’s phone number is used for a single call and then removed from the system. TrustboxAI does not retain, sell, or share any personal audio, contacts, or phone numbers.
      </>
    ),
  },
  {
    tag: 'PRIVACY',
    q: 'Is it legal to place a simulated scam call to my parent?',
    a: (
      <>
        Yes, when using TrustboxAI. The service is <strong>fully TCPA compliant</strong> &mdash; you are the one initiating the call on behalf of your family, and the simulation is strictly educational, not commercial or fraudulent. TrustboxAI handles all regulatory compliance so you don’t have to.
      </>
    ),
  },
  {
    tag: 'TRUST',
    q: 'Will the simulation frighten or traumatize my parent?',
    a: (
      <>
        TrustboxAI is engineered to <strong>educate, not scare</strong>. Every call ends with a calm, automated reveal: <em>“This was a safe test arranged by someone who loves you.”</em> Most families say the experience brought them closer and opened an important, collaborative conversation about phone safety. Your parent also receives a <strong>personalized report with practical tips</strong> &mdash; not just a shock.
      </>
    ),
  },
  {
    tag: 'TRUST',
    q: 'What happens if my family member doesn’t answer the call?',
    a: (
      <>
        If your family member doesn’t answer the TrustboxAI simulation call, you receive a <strong>full refund automatically</strong> &mdash; no questions asked. TrustboxAI does not leave voicemails or attempt multiple callbacks. You won’t be charged if the call doesn’t connect.
      </>
    ),
  },
  {
    tag: 'EDUCATIONAL',
    q: 'Can AI really clone a voice from just 3 seconds of audio?',
    a: (
      <>
        Yes. According to security research, modern AI tools can replicate a person’s voice with up to 85% accuracy from <strong>as little as 3 seconds of audio</strong>. Scammers harvest these samples from social media, voicemails, and public posts. TrustboxAI uses the same technology &mdash; but for <strong>education and defense</strong>, so your family can experience how convincing a clone sounds in a safe environment.
      </>
    ),
  },
  {
    tag: 'EDUCATIONAL',
    q: 'What is the grandparent scam and why is it more dangerous now?',
    a: (
      <>
        The grandparent scam is a fraud where criminals call an elderly person pretending to be a grandchild in urgent distress (claiming to be arrested, in an accident, etc.) and request money. <strong>AI voice cloning makes this incredibly convincing</strong> because the caller sounds exactly like the grandchild. FBI data shows seniors lost over <strong>$3.4 billion to fraud in 2023</strong>. TrustboxAI was built specifically to prepare families for this threat before a real call arrives. <Link href="/dashboard/simulate" style={{ color: '#166534', textDecoration: 'underline', fontWeight: 600, marginLeft: 4 }}>Run a safe simulation ($4.90) &rarr;</Link>
      </>
    ),
  },
];

const TAG_STYLES: Record<FaqTag, { bg: string; color: string; label: string }> = {
  BRAND: { bg: 'rgba(34,197,94,0.10)', color: '#15803D', label: 'Brand' },
  PRIVACY: { bg: 'rgba(59,130,246,0.10)', color: '#1D4ED8', label: 'Privacy' },
  TRUST: { bg: 'rgba(245,158,11,0.10)', color: '#B45309', label: 'Trust' },
  EDUCATIONAL: { bg: 'rgba(139,92,246,0.10)', color: '#6D28D9', label: 'Educational' },
};

function FAQAccordionItem({ faq, index, isVisible }: { faq: typeof FAQS[0]; index: number; isVisible: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const tagStyle = TAG_STYLES[faq.tag];

  return (
    <motion.div
      layout="position"
      style={{
        borderRadius: 20,
        background: 'white',
        border: '1px solid #E7E5E4',
        marginBottom: 16,
        overflow: 'hidden',
        boxShadow: isOpen ? '0 10px 25px -5px rgba(0,0,0,0.05)' : '0 2px 8px -2px rgba(0,0,0,0.02)',
        transition: 'box-shadow 0.3s, border-color 0.3s',
        display: isVisible ? 'block' : 'none',
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '20px 24px',
          background: 'none',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          outline: 'none',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
          <span
            style={{
              display: 'inline-block',
              padding: '3px 8px',
              borderRadius: 999,
              background: tagStyle.bg,
              color: tagStyle.color,
              fontSize: '0.625rem',
              fontWeight: 700,
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
            }}
          >
            {tagStyle.label}
          </span>
          <h3
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.0625rem',
              color: '#1C1917',
              fontWeight: 600,
              margin: 0,
              lineHeight: 1.4,
            }}
          >
            {faq.q}
          </h3>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: isOpen ? '#F0FDF4' : '#F5F5F4',
            color: isOpen ? '#22C55E' : '#57534E',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ChevronDown size={16} />
        </motion.div>
      </button>

      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        style={{ overflow: 'hidden' }}
      >
        <div style={{ padding: '0 24px 20px 24px', fontSize: '0.9375rem', color: '#57534E', lineHeight: 1.65 }}>
          <div style={{ width: '100%', height: 1, background: '#F5F5F4', marginBottom: 12 }} />
          {faq.a}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PAGE COMPONENT
   ═══════════════════════════════════════════════════════════════════ */

export default function LandingPage() {
  const shouldReduceMotion = useReducedMotion();
  const [bannerEnded, setBannerEnded] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<'ALL' | 'BRAND' | 'PRIVACY_TRUST' | 'EDUCATIONAL'>('ALL');

  useEffect(() => {
    const campaignEndTime = new Date('2026-05-10T00:00:00').getTime();
    if (Date.now() < campaignEndTime) {
      setBannerEnded(false);
    }
  }, []);

  // Design Partners Modal States
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [leadName, setLeadName] = useState('');
  const [leadOrg, setLeadOrg] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadUseCase, setLeadUseCase] = useState('');
  const [leadUsers, setLeadUsers] = useState('');
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);
  const [leadError, setLeadError] = useState('');

  // Auto Checkout effect
  useEffect(() => {
    async function handleAutoCheckout() {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('checkout') === 'annual') {
        trackEvent('checkout_initiated', { planType: 'annual', trigger: 'query_param' });

        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          try {
            const { url } = await api.createCheckoutSession(null, 'annual');
            if (url) {
              window.location.href = url;
            }
          } catch (err) {
            console.error('Failed to create annual checkout session', err);
          }
        } else {
          const currentUrl = window.location.pathname + window.location.search;
          window.location.href = `/login?next=${encodeURIComponent(currentUrl)}`;
        }
      }
    }
    handleAutoCheckout();
  }, []);

  const handleAnnualCheckout = async () => {
    trackEvent('checkout_initiated', { planType: 'annual', trigger: 'landing_pricing' });
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      try {
        const { url } = await api.createCheckoutSession(null, 'annual');
        if (url) {
          window.location.href = url;
        }
      } catch (err) {
        console.error('Failed to create annual checkout session', err);
      }
    } else {
      window.location.href = `/login?next=${encodeURIComponent('/?checkout=annual')}`;
    }
  };

  const handleSingleCheckout = () => {
    trackEvent('checkout_initiated', { planType: 'single', trigger: 'landing_pricing' });
    window.location.href = '/dashboard/simulate';
  };

  const handleOpenLeadModal = () => {
    setIsLeadModalOpen(true);
    setLeadSuccess(false);
    setLeadError('');
    trackEvent('landing_cta_clicked', { ctaLabel: 'design_partners_apply' });
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLeadLoading(true);
    setLeadError('');
    try {
      await api.submitLead({
        name: leadName,
        organization: leadOrg,
        email: leadEmail,
        useCase: leadUseCase,
        estimatedUsers: leadUsers,
      });
      setLeadSuccess(true);
      trackEvent('lead_form_submitted', { organization: leadOrg });
      // Reset fields
      setLeadName('');
      setLeadOrg('');
      setLeadEmail('');
      setLeadUseCase('');
      setLeadUsers('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to submit form';
      setLeadError(msg);
      trackEvent('lead_form_failed', { error: msg });
    } finally {
      setLeadLoading(false);
    }
  };

  const fadeUp = shouldReduceMotion ? {} : FADE_UP;
  const stagger = (delay = 0) => (shouldReduceMotion ? {} : makeStagger(delay));

  return (
    <main id="main-content" className="min-h-screen overflow-x-hidden grain">
      {/* Custom keyframes for this page */}
      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes aurora-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(0.85); opacity: 0.5; }
          50% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
        }
        @keyframes aurora-ring {
          0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.5; }
          100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
        }
        @keyframes tag-wave-pulse {
          0%, 100% { transform: scaleY(0.4); opacity: 0.5; }
          50% { transform: scaleY(1); opacity: 1; }
        }
        @keyframes gradient-text {
          0%, 100% { background-position: 0% center; }
          50% { background-position: 100% center; }
        }
        @keyframes cta-glow {
          0%, 100% { box-shadow: 0 0 0 rgba(34,197,94,0); }
          50% { box-shadow: 0 0 40px rgba(34,197,94,0.25); }
        }
        @keyframes connector-pulse {
          0%, 100% { transform: scaleY(0.4); }
          50% { transform: scaleY(1); }
        }
      `}} />

      <ScrollProgressBar />
      {!bannerEnded && <MothersDayCountdown onEnd={() => setBannerEnded(true)} />}
      <LandingNavbar topOffset={bannerEnded ? 0 : BANNER_HEIGHT} />

      {/* ══════════════════════════════════════════════════════════════
         ZONE 1: DARK-GREEN — Hero + Connector + Stats + Quote
         Continuous flow, no hard borders between hero and stats
         ══════════════════════════════════════════════════════════════ */}
      <div style={{ background: DARK_BG, position: 'relative', overflow: 'hidden' }}>
        {/* Aurora */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
          {/* Core blob */}
          <div
            style={{
              position: 'absolute',
              top: '40%',
              left: '50%',
              transform: 'translate(-50%, -40%)',
              width: '120vw',
              height: '200vh',
              borderRadius: '50%',
              background: 'radial-gradient(circle at center, rgba(30,185,84,0.6) 0%, rgba(30,185,84,0.4) 10%, rgba(30,185,84,0.2) 25%, rgba(30,185,84,0.06) 45%, transparent 65%)',
              filter: 'blur(30px)',
              animation: shouldReduceMotion ? 'none' : 'aurora-pulse 3.5s ease-in-out infinite',
            }}
          />
          {/* Expanding ring */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60vw',
              height: '60vh',
              borderRadius: '50%',
              border: '2px solid rgba(30,185,84,0.12)',
              animation: shouldReduceMotion ? 'none' : 'aurora-ring 4s ease-out infinite',
            }}
          />
        </div>

        {/* Grain texture */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            backgroundSize: '256px',
            opacity: 0.03,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />

        {/* ── HERO SECTION ── */}
        <section
          id="hero"
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            padding: '140px 48px 80px',
          }}
        >
          <div
            className="hero-grid"
            style={{
              position: 'relative',
              zIndex: 2,
              width: '100%',
              maxWidth: 1280,
              margin: '0 auto',
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 0.65fr)',
              gap: 48,
              alignItems: 'center',
            }}
          >
            {/* LEFT: Copy */}
            <header className="hero-left" style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Tag with animated waveform */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 28px',
                  borderRadius: 999,
                  background: 'rgba(30,185,84,0.12)',
                  border: '1px solid rgba(30,185,84,0.25)',
                  marginBottom: 32,
                  fontSize: '1.125rem',
                  color: '#86EFAC',
                  fontWeight: 600,
                  width: 'fit-content',
                  letterSpacing: '0.3px',
                }}
              >
                <TagWaveform />
                AI Voice Cloning &amp; Difficult Conversations Made Easy
              </motion.div>

              {/* Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(3rem, 5vw, 5.125rem)',
                  fontWeight: 700,
                  lineHeight: 1.05,
                  letterSpacing: '-2.5px',
                  color: '#F5F5F4',
                  marginBottom: 24,
                  textWrap: 'balance',
                }}
              >
                You&apos;ve tried warning mom.{' '}
                <span
                  style={{
                    background: 'linear-gradient(135deg, #4ADE80, #22C55E 50%, #06B6D4)',
                    backgroundSize: '300% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: shouldReduceMotion ? 'none' : 'gradient-text 5s ease-in-out infinite',
                  }}
                >
                  She doesn&apos;t get it.
                </span>
                {' '}Try this.
              </motion.h1>

              {/* Subline */}
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                style={{
                  fontSize: 'clamp(1.125rem, 2.2vw, 1.375rem)',
                  color: '#C4C0BB',
                  maxWidth: 580,
                  lineHeight: 1.65,
                  marginBottom: 16,
                }}
              >
                No more dreading the &ldquo;security talk.&rdquo; A safe, simulated call in your cloned voice acts as a warm, interactive icebreaker to agree on a family safeword without the lecturing.
              </motion.p>

              {/* Core scannable benefits checklist */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '12px 20px',
                  marginBottom: 32,
                }}
              >
                {[
                  '15-Second Recording',
                  'TCPA Compliant & Safe',
                  'Voice Deleted in 24h',
                  'US & Canada Only',
                ].map((text) => (
                  <span
                    key={text}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: '0.875rem',
                      color: '#86EFAC',
                      fontWeight: 500,
                    }}
                  >
                    <Check size={14} strokeWidth={3} style={{ color: '#4ADE80' }} />
                    {text}
                  </span>
                ))}
              </motion.div>

              {/* CTA area */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
                className="hero-cta-area"
                style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}
              >
                <Link
                  href="/dashboard/simulate"
                  onClick={() =>
                    trackEvent('landing_cta_clicked', {
                      ctaLabel: 'hero_run_simulation',
                      destination: '/dashboard/simulate',
                    })
                  }
                  className="focus-visible:outline focus-visible:outline-3 focus-visible:outline-[#4ADE80] focus-visible:outline-offset-[3px]"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '18px 40px',
                    borderRadius: 999,
                    background: '#004322',
                    color: 'white',
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    width: 'fit-content',
                    animation: shouldReduceMotion ? 'none' : 'cta-glow 2.5s ease-in-out infinite',
                    transition: 'transform 0.3s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
                >
                  Run a Safe Simulation ($4.90)
                  <ArrowRight size={20} />
                </Link>

                <Link
                  href="/try"
                  onClick={() =>
                    trackEvent('landing_cta_clicked', {
                      ctaLabel: 'hero_try_demo',
                      destination: '/try',
                    })
                  }
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    color: '#86EFAC',
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    textDecoration: 'none',
                    width: 'fit-content',
                    paddingLeft: 4,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                >
                  or try the free voice clone demo first &rarr;
                </Link>

                <p style={{ fontSize: '0.9375rem', color: 'rgba(255,255,255,0.3)' }}>
                  One-time simulation. No subscription required. 100% refund if they don&apos;t answer.
                </p>
              </motion.div>

              {/* Spacer before header end */}
              <div style={{ height: 1 }} />
            </header>

            {/* RIGHT: Phone Simulator */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
            >
              <PhoneSimulator />
            </motion.div>
          </div>
        </section>

        {/* ── Connector: hero -> stats ── */}
        <SectionConnector color="#22C55E" />

        {/* ── STATS SECTION ── */}
        <div
          style={{
            padding: '0 24px 100px',
            maxWidth: 1000,
            margin: '0 auto',
            position: 'relative',
            zIndex: 2,
          }}
        >
          {/* Section header */}
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <motion.p
              {...fadeUp}
              style={{
                fontSize: '0.875rem',
                letterSpacing: 3,
                textTransform: 'uppercase',
                fontWeight: 600,
                marginBottom: 14,
                color: 'rgba(34,197,94,0.5)',
              }}
            >
              Why this matters
            </motion.p>
            <motion.h2
              {...stagger(0.1)}
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                lineHeight: 1.08,
                letterSpacing: '-1px',
                color: '#F5F5F4',
              }}
            >
              Why talking to aging parents
              <br />
              about scams is so hard.
            </motion.h2>
          </div>

          {/* 3 stat cards */}
          <div
            className="sg"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 24,
              marginBottom: 60,
            }}
          >
            {[
              { value: 3.4, prefix: '$', suffix: 'B', decimals: 1, label: 'The Silence Gap: Lost to fraud because parents are too embarrassed to report or talk about scams.', source: 'FBI IC3' },
              { value: 70, prefix: '', suffix: '%', decimals: 0, label: 'The Overconfidence Barrier: Seniors who believe they could never be tricked until they hear a clone.', source: 'McAfee' },
              { value: 3, prefix: '', suffix: ' sec', decimals: 0, label: 'The Realism Shock: Time it takes to clone a voice. Hearing is the only way to break denial.', source: 'McAfee' },
            ].map((stat, i) => (
              <motion.div
                key={stat.source + stat.suffix}
                {...stagger(i * 0.2)}
                style={{
                  padding: '40px 28px',
                  borderRadius: 22,
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  textAlign: 'center',
                  transition: 'all 0.4s cubic-bezier(0.16,1,0.3,1)',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 24px 48px rgba(0,0,0,0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = '';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <p
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'clamp(3rem, 7vw, 5rem)',
                    fontWeight: 700,
                    color: '#4ADE80',
                    lineHeight: 1,
                    marginBottom: 14,
                  }}
                >
                  <CountUp
                    target={stat.value}
                    prefix={stat.prefix}
                    suffix={stat.suffix}
                    decimals={stat.decimals}
                  />
                </p>
                <p style={{ fontSize: '1.125rem', color: '#D4D0CC', lineHeight: 1.6 }}>
                  {stat.label}
                </p>
                <p style={{ fontSize: '0.9375rem', color: 'rgba(255,255,255,0.35)', marginTop: 12 }}>
                  {stat.source}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Quote — adds an H2 anchor for AI scam simulation social proof */}
          <motion.h2
            {...fadeUp}
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 'clamp(1.25rem, 2.4vw, 1.625rem)',
              lineHeight: 1.3,
              letterSpacing: '-0.3px',
              color: 'rgba(255,255,255,0.55)',
              textAlign: 'center',
              maxWidth: 720,
              margin: '0 auto 8px',
            }}
          >
            What families say about AI voice scam simulations
          </motion.h2>
          <motion.blockquote
            {...fadeUp}
            style={{
              maxWidth: 600,
              margin: '0 auto',
              textAlign: 'center',
              position: 'relative',
              padding: '48px 24px',
            }}
          >
            {/* Decorative quotation mark */}
            <div
              style={{
                position: 'absolute',
                top: -20,
                left: '50%',
                transform: 'translateX(-50%)',
                fontFamily: 'var(--font-display)',
                fontSize: '10rem',
                color: 'rgba(34,197,94,0.05)',
                lineHeight: 1,
                pointerEvents: 'none',
                userSelect: 'none',
              }}
              aria-hidden="true"
            >
              &ldquo;
            </div>
            <p
              style={{
                fontSize: '1.75rem',
                fontStyle: 'italic',
                color: '#F5F5F4',
                lineHeight: 1.55,
                position: 'relative',
                zIndex: 1,
              }}
            >
              &ldquo;I dreaded bringing this up. My parents hate being lectured. But when the Trustbox call ended and we talked, they actually thanked me for making it so easy.&rdquo;
            </p>
            <cite
              style={{
                display: 'block',
                fontSize: '1rem',
                color: '#A8A29E',
                marginTop: 16,
                fontStyle: 'normal',
              }}
            >
              &mdash; Clara, 41 (Verified Trustbox User)
            </cite>
          </motion.blockquote>

        </div>

        {/* ── HOW IT WORKS & VOICE CHALLENGE ── */}
      </div>

      {/* ══════════════════════════════════════════════════════════════
         ZONE 2: CREAM — How It Works + Pricing + Objections
         ══════════════════════════════════════════════════════════════ */}
      <div style={{ background: CREAM_BG, position: 'relative', marginTop: -1 }}>
        {/* SVG curve transition: dark -> cream */}
        <div style={{ position: 'absolute', top: -90, left: 0, right: 0, height: 90, zIndex: 4 }}>
          <svg
            viewBox="0 0 1440 90"
            preserveAspectRatio="none"
            style={{ width: '100%', height: '100%', display: 'block' }}
          >
            <path fill={CREAM_BG} d="M0,90 Q720,-10 1440,90 L1440,90 L0,90 Z" />
          </svg>
        </div>

        {/* Connector waveform (cream zone) */}
        <SectionConnector color="#004322" />

        {/* ── HOW IT WORKS ── */}
        <section id="how-it-works" style={{ padding: '80px 24px 80px' }}>
          <div
            style={{
              maxWidth: 1200,
              margin: '0 auto',
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <motion.p
                {...fadeUp}
                style={{
                  fontSize: '0.875rem',
                  letterSpacing: 3,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  marginBottom: 14,
                  color: '#004322',
                }}
              >
                How It Works
              </motion.p>
              <motion.h2
                {...stagger(0.1)}
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 'clamp(2rem, 5vw, 3rem)',
                  lineHeight: 1.08,
                  letterSpacing: '-1px',
                  color: '#1C1917',
                }}
              >
                4 simple steps
                <br />
                to start the conversation.
              </motion.h2>
            </div>

            {/* Two-Column Grid: Steps + Voice Challenge */}
            <div
              className="howclone-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: '1.1fr 0.9fr',
                gap: 48,
                alignItems: 'start',
              }}
            >
              {/* Left: Vertical Timeline */}
              <div style={{ position: 'relative', paddingLeft: 52 }}>
                {/* Green line */}
                <div
                  style={{
                    position: 'absolute',
                    left: 16,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    background: 'linear-gradient(to bottom, #22C55E, #E7E5E4)',
                  }}
                />

                {STEPS.map((step, i) => (
                  <motion.div
                    key={step.num}
                    {...stagger(i * 0.15)}
                    style={{
                      position: 'relative',
                      paddingBottom: i < STEPS.length - 1 ? 40 : 0,
                    }}
                  >
                    {/* Numbered dot */}
                    <div
                      style={{
                        position: 'absolute',
                        left: -52,
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        background: 'white',
                        border: '2px solid #22C55E',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'var(--font-display)',
                        fontSize: '0.9375rem',
                        fontWeight: 700,
                        color: '#004322',
                        boxShadow: `0 0 0 6px ${CREAM_BG}`,
                      }}
                    >
                      {step.num}
                    </div>
                    <h3
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: '1.375rem',
                        fontWeight: 600,
                        color: '#1C1917',
                        marginBottom: 8,
                      }}
                    >
                      {step.title}
                    </h3>
                    <p style={{ fontSize: '1rem', color: '#57534E', lineHeight: 1.65, margin: 0 }}>
                      {step.body}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Right: Interactive Voice Challenge */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.8, ease: EASE }}
                style={{ position: 'relative' }}
              >
                <div
                  style={{
                    padding: '32px 28px',
                    borderRadius: 24,
                    background: 'white',
                    border: '1px solid #E7E5E4',
                    boxShadow: '0 10px 30px -10px rgba(0,0,0,0.04)',
                  }}
                >
                  <h3
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#1C1917',
                      marginBottom: 8,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <Sparkles size={18} style={{ color: '#22C55E' }} />
                    Experience AI Realism
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#57534E', lineHeight: 1.5, marginBottom: 20 }}>
                    Listen to real voice samples and test your own hearing. Can you detect which audio is a real human and which is a synthesized AI voice clone?
                  </p>
                  <VoiceChallenge />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── RISK QUIZ SECTION ── */}
        <section id="risk-quiz" style={{ padding: '40px 24px 60px', background: CREAM_BG }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <motion.p
                {...fadeUp}
                style={{
                  fontSize: '0.875rem',
                  letterSpacing: 3,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  marginBottom: 14,
                  color: '#004322',
                }}
              >
                Assess Your Vulnerability
              </motion.p>
              <motion.h2
                {...stagger(0.1)}
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 'clamp(2rem, 5vw, 2.75rem)',
                  lineHeight: 1.1,
                  letterSpacing: '-1px',
                  color: '#1C1917',
                }}
              >
                Is Your Family Prepared for a Voice Clone?
              </motion.h2>
            </div>
            <FamilyRiskQuiz />
          </div>
        </section>

        {/* ── PRICING SECTION (3 Columns) ── */}
        <section id="pricing" className="scroll-mt-24" style={{ padding: '60px 24px 100px' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <motion.p
                {...fadeUp}
                style={{
                  fontSize: '0.875rem',
                  letterSpacing: 3,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  marginBottom: 14,
                  color: '#004322',
                }}
              >
                Choose Your Protection Plan
              </motion.p>
              <motion.h2
                {...stagger(0.1)}
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 'clamp(2rem, 5vw, 3rem)',
                  lineHeight: 1.08,
                  letterSpacing: '-1px',
                  color: '#1C1917',
                  marginBottom: 18,
                }}
              >
                Flexible defense options for every family.
              </motion.h2>
            </div>

            <div
              className="pricing-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 28,
                alignItems: 'stretch',
              }}
            >
              {/* Column 1: Single Check */}
              <motion.div
                {...stagger(0.1)}
                style={{
                  padding: '40px 32px',
                  borderRadius: 24,
                  background: 'white',
                  border: '1px solid #E7E5E4',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#57534E', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: 12 }}>
                    Single Check
                  </span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 18 }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', fontWeight: 700, color: '#1C1917' }}>$4.90</span>
                    <span style={{ fontSize: '1rem', color: '#57534E' }}>/simulation</span>
                  </div>
                  <p style={{ fontSize: '1rem', color: '#57534E', marginBottom: 24, minHeight: 48 }}>
                    Ideal for a single, immediate checkup to verify the awareness of a loved one.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[
                      'AI voice clone of your voice',
                      'Real phone call to your family member',
                      'Awareness score + safety report',
                      'Voice data deleted within 24 hours',
                    ].map((item) => (
                      <div key={item} style={{ display: 'flex', alignItems: 'start', gap: 10, padding: '2px 0' }}>
                        <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22C55E', flexShrink: 0, marginTop: 2 }}>
                          <Check size={10} strokeWidth={3} />
                        </span>
                        <span style={{ fontSize: '0.9375rem', color: '#57534E' }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleSingleCheckout}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '14px 24px',
                    marginTop: 32,
                    borderRadius: 999,
                    background: 'white',
                    color: '#004322',
                    border: '2px solid #004322',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#004322'; e.currentTarget.style.color = 'white'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#004322'; }}
                >
                  Start Single Check
                </button>
              </motion.div>

              {/* Column 2: Annual Safeguard (Recommended) */}
              <motion.div
                {...stagger(0.2)}
                style={{
                  padding: '40px 32px',
                  borderRadius: 24,
                  background: 'white',
                  border: '2px solid #22C55E',
                  boxShadow: '0 12px 32px rgba(34,197,94,0.12)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div style={{ position: 'absolute', top: 14, right: 14, padding: '4px 12px', borderRadius: 999, background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', color: '#166534', fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Sparkles size={11} /> Recommended
                </div>

                <div>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#22C55E', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: 12 }}>
                    Annual Safeguard
                  </span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', fontWeight: 700, color: '#004322' }}>$3.90</span>
                    <span style={{ fontSize: '1rem', color: '#57534E', fontWeight: 500 }}>/month</span>
                  </div>
                  <span style={{ fontSize: '0.8125rem', color: '#166534', display: 'block', marginBottom: 18, fontWeight: 500 }}>
                    Billed annually ($46.80/year)
                  </span>
                  <p style={{ fontSize: '1rem', color: '#57534E', marginBottom: 24, minHeight: 48 }}>
                    Protect your entire household with multiple simulations and year-round monitoring updates.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[
                      '6 family simulations included',
                      'Add and protect multiple family members',
                      '1 year of continuous defense monitoring',
                      'Safe reveal call & awareness reports',
                      'Full voice data deletion within 24 hours',
                    ].map((item) => (
                      <div key={item} style={{ display: 'flex', alignItems: 'start', gap: 10, padding: '2px 0' }}>
                        <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22C55E', flexShrink: 0, marginTop: 2 }}>
                          <Check size={10} strokeWidth={3} />
                        </span>
                        <span style={{ fontSize: '0.9375rem', color: '#57534E' }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleAnnualCheckout}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '14px 24px',
                    marginTop: 32,
                    borderRadius: 999,
                    background: '#004322',
                    color: 'white',
                    border: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textAlign: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(0,67,34,0.3)',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
                >
                  Get Annual Protection
                </button>
              </motion.div>

              {/* Column 3: Design Partners */}
              <motion.div
                {...stagger(0.3)}
                style={{
                  padding: '40px 32px',
                  borderRadius: 24,
                  background: 'white',
                  border: '1px solid #E7E5E4',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#57534E', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: 12 }}>
                    Design Partners
                  </span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 18 }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '2.75rem', fontWeight: 700, color: '#1C1917' }}>Institutional</span>
                  </div>
                  <p style={{ fontSize: '1rem', color: '#57534E', marginBottom: 24, minHeight: 48 }}>
                    For organizations, schools, or senior groups looking to deploy large-scale defense simulations.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[
                      'Shared organizational dashboard',
                      'Volume-discounted simulation packs',
                      'Custom templates & training guides',
                      'Dedicated integration support specialist',
                      'Co-branded educational reports',
                    ].map((item) => (
                      <div key={item} style={{ display: 'flex', alignItems: 'start', gap: 10, padding: '2px 0' }}>
                        <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22C55E', flexShrink: 0, marginTop: 2 }}>
                          <Check size={10} strokeWidth={3} />
                        </span>
                        <span style={{ fontSize: '0.9375rem', color: '#57534E' }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleOpenLeadModal}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '14px 24px',
                    marginTop: 32,
                    borderRadius: 999,
                    background: 'white',
                    color: '#57534E',
                    border: '2px solid #E7E5E4',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#57534E'; e.currentTarget.style.color = '#1C1917'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E7E5E4'; e.currentTarget.style.color = '#57534E'; }}
                >
                  Apply to Program
                </button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Design Partners Modal */}
        {isLeadModalOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            {/* Backdrop */}
            <div
              onClick={() => setIsLeadModalOpen(false)}
              style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(8px)' }}
            />

            {/* Modal Container */}
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                maxWidth: 520,
                background: '#04150b',
                border: '1px solid rgba(30, 185, 84, 0.3)',
                borderRadius: 28,
                padding: '36px 32px',
                boxShadow: '0 25px 50px -12px rgba(30, 185, 84, 0.25)',
                color: 'white',
              }}
            >
              {/* Close Button */}
              <button
                onClick={() => setIsLeadModalOpen(false)}
                style={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
              >
                <X size={16} />
              </button>

              {leadSuccess ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(30, 185, 84, 0.1)', border: '2px solid #1EB954', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', color: '#1EB954' }}>
                    <Check size={32} strokeWidth={3} />
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.625rem', fontWeight: 700, marginBottom: 12 }}>Application Submitted!</h3>
                  <p style={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, marginBottom: 28 }}>
                    Thank you for applying to be a Design Partner. Our team will review your application and reach out within 2 business days.
                  </p>
                  <button
                    onClick={() => setIsLeadModalOpen(false)}
                    style={{
                      padding: '12px 32px',
                      borderRadius: 999,
                      background: '#1EB954',
                      color: 'white',
                      border: 'none',
                      fontSize: '0.9375rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    Close Window
                  </button>
                </div>
              ) : (
                <form onSubmit={handleLeadSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, marginBottom: 6 }}>Design Partners Program</h3>
                    <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.5)', lineHeight: 1.5 }}>
                      Help us shape the future of family voice security. Submit your organization details below.
                    </p>
                  </div>

                  {leadError && (
                    <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#fca5a5', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Info size={16} /> {leadError}
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>Your Name</label>
                    <input
                      type="text"
                      required
                      value={leadName}
                      onChange={(e) => setLeadName(e.target.value)}
                      placeholder="Jane Doe"
                      style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', outline: 'none', fontSize: '0.9375rem' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>Organization Name</label>
                    <input
                      type="text"
                      required
                      value={leadOrg}
                      onChange={(e) => setLeadOrg(e.target.value)}
                      placeholder="Eldercare Association"
                      style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', outline: 'none', fontSize: '0.9375rem' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>Work Email</label>
                    <input
                      type="email"
                      required
                      value={leadEmail}
                      onChange={(e) => setLeadEmail(e.target.value)}
                      placeholder="jane@eldercare.org"
                      style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', outline: 'none', fontSize: '0.9375rem' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>Core Use Case</label>
                    <textarea
                      required
                      rows={3}
                      value={leadUseCase}
                      onChange={(e) => setLeadUseCase(e.target.value)}
                      placeholder="We want to run educational workshops for our 500+ senior members..."
                      style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', outline: 'none', fontSize: '0.9375rem', resize: 'none' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>Estimated Users / Members</label>
                    <input
                      type="text"
                      required
                      value={leadUsers}
                      onChange={(e) => setLeadUsers(e.target.value)}
                      placeholder="e.g. 500-1000"
                      style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', outline: 'none', fontSize: '0.9375rem' }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={leadLoading}
                    style={{
                      marginTop: 8,
                      padding: '14px 24px',
                      borderRadius: 999,
                      background: 'linear-gradient(135deg, #1EB954 0%, #178e41 100%)',
                      color: 'white',
                      border: 'none',
                      fontSize: '1rem',
                      fontWeight: 700,
                      cursor: leadLoading ? 'not-allowed' : 'pointer',
                      opacity: leadLoading ? 0.7 : 1,
                      textAlign: 'center',
                      boxShadow: '0 8px 24px -8px rgba(30,185,84,0.4)',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => { if (!leadLoading) e.currentTarget.style.transform = 'scale(1.02)'; }}
                    onMouseLeave={(e) => { if (!leadLoading) e.currentTarget.style.transform = ''; }}
                  >
                    {leadLoading ? 'Submitting...' : 'Submit Application'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* ── OBJECTIONS SECTION ── */}
        <section style={{ padding: '0 24px 100px' }}>
          <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            {/* Section header */}
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <motion.p
                {...fadeUp}
                style={{
                  fontSize: '0.875rem',
                  letterSpacing: 3,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  marginBottom: 14,
                  color: '#004322',
                }}
              >
                FAQ & Security
              </motion.p>
              <motion.h2
                {...stagger(0.1)}
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
                  lineHeight: 1.08,
                  letterSpacing: '-1px',
                  color: '#1C1917',
                }}
              >
                Frequently asked questions about TrustboxAI.
              </motion.h2>
            </div>

            {/* Category tabs */}
            <div 
              style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                flexWrap: 'wrap', 
                gap: 8, 
                marginBottom: 32,
                maxWidth: 800,
                margin: '0 auto 32px auto',
                padding: '0 8px'
              }}
            >
              {[
                { id: 'ALL', label: 'All FAQs' },
                { id: 'BRAND', label: 'Brand & Cost' },
                { id: 'PRIVACY_TRUST', label: 'Privacy & Safety' },
                { id: 'EDUCATIONAL', label: 'Scams & Education' }
              ].map((tab) => {
                const isActive = selectedCategory === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSelectedCategory(tab.id as any)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 999,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      background: isActive ? '#004322' : 'white',
                      color: isActive ? 'white' : '#57534E',
                      border: isActive ? '1px solid #004322' : '1px solid #E7E5E4',
                      boxShadow: isActive 
                        ? '0 4px 12px -2px rgba(0,67,34,0.15)' 
                        : '0 1px 2px rgba(0,0,0,0.02)',
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* 10-question FAQ — full visible content matches FAQPage schema for AI citation */}
            <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 8px' }}>
              {FAQS.map((faq, i) => {
                const isVisible = selectedCategory === 'ALL' ||
                  (selectedCategory === 'BRAND' && faq.tag === 'BRAND') ||
                  (selectedCategory === 'PRIVACY_TRUST' && (faq.tag === 'PRIVACY' || faq.tag === 'TRUST')) ||
                  (selectedCategory === 'EDUCATIONAL' && faq.tag === 'EDUCATIONAL');
                return (
                  <FAQAccordionItem key={faq.q} faq={faq} index={i} isVisible={isVisible} />
                );
              })}
            </div>
          </div>
        </section>

        {/* ── LATEST FROM LEARN — internal links + topical density for AI crawlers ── */}
        <section id="latest-from-learn" style={{ padding: '0 24px 120px' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
              <div>
                <motion.p
                  {...fadeUp}
                  style={{
                    fontSize: '0.875rem',
                    letterSpacing: 3,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    marginBottom: 10,
                    color: '#004322',
                  }}
                >
                  Latest from the Learn hub
                </motion.p>
                <motion.h2
                  {...stagger(0.1)}
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: 'clamp(1.625rem, 3.5vw, 2.25rem)',
                    lineHeight: 1.15,
                    letterSpacing: '-0.5px',
                    color: '#1C1917',
                  }}
                >
                  Real cases, prevention guides, and the research behind voice scams.
                </motion.h2>
              </div>
              <Link
                href="/learn"
                onClick={() =>
                  trackEvent('landing_cta_clicked', {
                    ctaLabel: 'latest_from_learn_see_all',
                    destination: '/learn',
                  })
                }
                style={{
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  color: '#004322',
                  textDecoration: 'none',
                  borderBottom: '1px solid #004322',
                  paddingBottom: 2,
                }}
              >
                See all {learnArticles.length} articles &rarr;
              </Link>
            </div>

            <div
              className="learn-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 20,
              }}
            >
              {[...learnArticles]
                .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
                .slice(0, 3)
                .map((article, i) => {
                  const cat = CATEGORY_META[article.category];
                  return (
                    <motion.article
                      key={article.slug}
                      {...stagger(i * 0.1)}
                      style={{
                        padding: 24,
                        borderRadius: 22,
                        background: 'white',
                        border: '1px solid #E7E5E4',
                        transition: 'all 0.3s',
                      }}
                    >
                      <Link
                        href={`/learn/${article.slug}`}
                        onClick={() =>
                          trackEvent('landing_cta_clicked', {
                            ctaLabel: `latest_from_learn_${article.slug}`,
                            destination: `/learn/${article.slug}`,
                          })
                        }
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 12,
                          textDecoration: 'none',
                        }}
                      >
                        <span
                          style={{
                            alignSelf: 'flex-start',
                            padding: '4px 10px',
                            borderRadius: 999,
                            background: `${cat.color}15`,
                            color: cat.color,
                            fontSize: '0.6875rem',
                            fontWeight: 700,
                            letterSpacing: '0.6px',
                            textTransform: 'uppercase',
                          }}
                        >
                          {cat.label}
                        </span>
                        <h3
                          style={{
                            fontFamily: 'var(--font-display)',
                            fontSize: '1.1875rem',
                            fontWeight: 600,
                            color: '#1C1917',
                            lineHeight: 1.3,
                          }}
                        >
                          {article.title}
                        </h3>
                        <p style={{ fontSize: '0.875rem', color: '#57534E', lineHeight: 1.6 }}>
                          {article.description}
                        </p>
                        <span style={{ fontSize: '0.75rem', color: '#A8A29E', marginTop: 4 }}>
                          {article.readTimeMinutes} min read
                        </span>
                      </Link>
                    </motion.article>
                  );
                })}
            </div>
          </div>
        </section>
      </div>

      {/* ══════════════════════════════════════════════════════════════
         ZONE 3: DARK-GREEN — Footer
         ══════════════════════════════════════════════════════════════ */}
      <div style={{ background: DARK_BG, position: 'relative', marginTop: -1 }}>
        {/* SVG curve transition: cream -> dark */}
        <div style={{ position: 'absolute', top: -90, left: 0, right: 0, height: 90, zIndex: 4 }}>
          <svg
            viewBox="0 0 1440 90"
            preserveAspectRatio="none"
            style={{ width: '100%', height: '100%', display: 'block' }}
          >
            <path fill={DARK_BG} d="M0,90 Q720,-10 1440,90 L1440,90 L0,90 Z" />
          </svg>
        </div>

        <LandingFooter />
      </div>

      {/* ── Responsive styles ── */}
      <style dangerouslySetInnerHTML={{
        __html: `
        @media (max-width: 900px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            text-align: center !important;
          }
          .hero-left {
            align-items: center !important;
          }
          .hero-cta-area {
            align-items: center !important;
          }
          .sg {
            grid-template-columns: 1fr !important;
          }
          .s2-in {
            grid-template-columns: 1fr !important;
          }
          .pc {
            position: static !important;
          }
          .obj-grid {
            grid-template-columns: 1fr !important;
          }
          .howclone-grid {
            grid-template-columns: 1fr !important;
          }
          .learn-grid {
            grid-template-columns: 1fr !important;
          }
          .pricing-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
          #hero {
            padding: 80px 20px 60px !important;
          }
        }
      `}} />
    </main>
  );
}
